import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { connect } from 'unistore/full/react';
import actions from './actions';
import getImagesUrlBySize from './get-images-url-by-size';
import getTypeFromUri from './get-type-from-uri';
import './NowPlayingView.scss';
import PlaybackProgress from './PlaybackProgress';
import Queue from './Queue';
import truncate from './truncate';

export default connect(
  ({
    currentRow,
    contextUri,
    addedStateByUri,
    trackRowRecommendationsByContextUri,
    trackFeedbackByUri,
    playerState: {
      paused,
      track_window: { current_track: currentTrack } = {},
      context: currentContext,
    } = {},
    colorsByUri,
    isNowPlayingViewOpen,
    preview: { uri: currentPreviewUri, isPlaying } = {},
    playlistsById,
    stationsById,
    isPlayerReady,
    contextPlayerState,
    isShuffleEnabled,
    isPremium,
    adaptiveSignalByContextUriAndTrackUri,
  }) => {
    const currentRecommendationsUri =
      currentRow?.track?.uri + currentContext?.uri;

    const stationId = currentContext?.uri?.match(
      /^spotify:internal:station:([^:]+)$/
    )?.[1];

    const currentTrackColors =
      colorsByUri[currentTrack?.linked_from?.uri || currentTrack?.uri];
    return {
      currentRow,
      currentContext,
      contextUri,
      addedStateByUri,
      trackRowRecommendationsByContextUri,
      trackFeedbackByUri,
      trackRowRecommendations:
        trackRowRecommendationsByContextUri[currentRecommendationsUri],
      isPlaying: !paused && !!contextUri,
      trackAlbumImages: currentTrack?.album?.images,
      currentTrackColors,
      currentRecommendationsUri,
      isNowPlayingViewOpen,
      currentPreviewUri,
      isPreviewPlaying: isPlaying,
      currentFeedback: trackFeedbackByUri[currentRow?.track?.uri],
      currentContextDescription:
        currentContext?.metadata?.context_description ||
        (/playlist:[A-Z0-f]{22}$/i.test(currentContext?.uri) &&
          playlistsById[currentContext?.uri?.substr(-22)]?.name) ||
        stationsById[stationId]?.name,
      futureTracks: stationsById[stationId]?.futureTracks,
      isPlayerReady,
      isShuffleEnabled,
      isRecommendation:
        typeof contextPlayerState?.track?.metadata?.source === 'string' &&
        contextPlayerState?.track?.metadata?.source !== 'track-seeds',
      isPremium,
      isAdded: addedStateByUri[currentRow?.track?.uri],

      isInspiredByMix:
        getTypeFromUri(currentContext?.uri) === 'playlist' &&
        playlistsById[currentContext?.uri?.substr(-22)]?.format ===
          'inspiredby-mix',

      currentContextTrackAdaptiveSignal:
        adaptiveSignalByContextUriAndTrackUri?.[contextUri]?.[
          currentRow?.track?.uri
        ],
    };
  },
  actions
)(NowPlayingView);

function NowPlayingView(props) {
  const history = useHistory();

  let {
    currentRow,
    currentContext,
    isPlaying,
    trackAlbumImages = [],
    currentTrackColors,
    trackRowRecommendations,
    currentRecommendationsUri,
    isNowPlayingViewOpen,
    currentPreviewUri,
    isPreviewPlaying,
    currentFeedback,
    currentContextDescription,
    futureTracks,
    isPlayerReady,
    isRecommendation,
    isShuffleEnabled,
    isPremium,
    isAdded,
    isInspiredByMix,
    currentContextTrackAdaptiveSignal,
  } = props;

  const isStation = /^spotify:internal:station/.test(currentContext?.uri);

  const stationId = currentContext?.uri?.match(
    /^spotify:internal:station:([^:]+)$/
  )?.[1];

  useEffect(() => {
    if (isNowPlayingViewOpen) {
      if (/playlist:[A-Z0-f]{22}$/i.test(currentContext?.uri))
        props.fetchPlaylist(currentContext?.uri?.substr(-22));
    }
  }, [currentContext?.uri, isNowPlayingViewOpen]);

  const [isQueueVisible, setIsQueueVisible] = useState(false);

  return (
    <div
      className={`NowPlayingView ${
        isNowPlayingViewOpen ? 'NowPlayingView--is-open' : ''
      }`}
      style={{ backgroundColor: currentTrackColors?.VIBRANT || '#282828' }}
    >
      {isQueueVisible && <Queue onClose={() => setIsQueueVisible(false)} />}
      <div className="NowPlayingView__content">
        <div
          className="NowPlayingView__svg-wrapper"
          style={{
            display: isQueueVisible ? 'none' : 'flex',
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
            fill="none"
            viewBox="0 0 375 667"
            className="NowPlayingView__main-svg"
          >
            <g id="cover-art">
              <path
                fill="#282828"
                d="M42.684 80H333.83000000000004V371H42.684z"
              ></path>
              <image
                width="291"
                height="291"
                x="42.684"
                y="80"
                xlinkHref={getImagesUrlBySize(trackAlbumImages, 291)}
              ></image>
            </g>

            <foreignObject
              x="8"
              y="424"
              width="360"
              height="62"
              id="playback-progress"
            >
              <PlaybackProgress />
            </foreignObject>

            <g id="bottom-bar" transform="translate(0, -137)">
              <g
                pointerEvents="bounding-box"
                onClick={() => setIsQueueVisible(true)}
              >
                <rect
                  width="60"
                  height="60"
                  fill="#000"
                  x="310"
                  y="689"
                  fillOpacity="0.001"
                />

                {isRecommendation ? (
                  <g
                    transform="translate(1, 706)"
                    id="recommendation-queue-button"
                  >
                    <path
                      fill="#1DB954"
                      fillRule="evenodd"
                      d="M336.286 6.418L335.5 4l-.786 2.418h-2.543l2.058 1.495-.786 2.419 2.057-1.495 2.057 1.495-.786-2.419 2.058-1.495h-2.543zM348.083 19.5h-14.166v-.834h14.166v.834zm-14.166-5.834h14.166v-.833h-14.166v.833zm6.666-5.833V7h7.5v.833h-7.5z"
                      clipRule="evenodd"
                    ></path>

                    <text
                      fill="#1DB954"
                      style={{ whiteSpace: 'pre' }}
                      fontSize="11"
                      letterSpacing="0em"
                    >
                      <tspan x="217.422" y="16.602">
                        Recommended song
                      </tspan>
                    </text>
                  </g>
                ) : (
                  <g id="queue-button">
                    <path
                      fill="#fff"
                      fillRule="evenodd"
                      d="M334.917 725.5h14.166v-.833h-14.166v.833zm0-14.583v5l4.33-2.5-4.33-2.5zm0 8.75h14.166v-.834h-14.166v.834zm6.666-6.667v.833h7.5V713h-7.5z"
                      clipRule="evenodd"
                    ></path>
                  </g>
                )}
              </g>

              <g pointerEvents="bounding-box">
                <rect
                  width="60"
                  height="60"
                  fill="#000"
                  x="6"
                  y="689"
                  fillOpacity="0.001"
                />
                <path
                  fill="#fff"
                  fillRule="evenodd"
                  d="M25.833 711.75v10c0 .458.375.833.834.833h5.832v-.833h-5.832v-10h5.832v-.833h-5.832a.836.836 0 00-.834.833zm3.333 12.917c0 .229.187.416.416.416H32.5v-.833h-2.917a.418.418 0 00-.416.417zm14.167-13.75h-7.5a.836.836 0 00-.833.833v12.5c0 .458.375.833.833.833h7.5a.836.836 0 00.834-.833v-12.5a.836.836 0 00-.834-.833zm-7.486 13.333l-.014-12.487h7.487l.013 12.474-7.486.013zm3.736-6.667a2.5 2.5 0 10.001 5 2.5 2.5 0 000-5zm0 4.167c-.919 0-1.666-.747-1.666-1.667a1.668 1.668 0 013.333 0c0 .92-.748 1.667-1.667 1.667zm0-6.667a.833.833 0 100-1.667.833.833 0 000 1.667z"
                  clipRule="evenodd"
                ></path>
              </g>
            </g>

            <g id="player-controls" transform="translate(0, -135)">
              <g>
                <g transform="translate(328, 646)">
                  {isInspiredByMix ? (
                    currentContextTrackAdaptiveSignal === 'like' ? (
                      <g
                        id="thumbs-up-filled"
                        onClick={() => {
                          // props.sendAdaptiveRadioSignal(
                          //   'like',
                          //   currentContext?.uri,
                          //   currentRow?.track?.uri,
                          // );
                        }}
                      >
                        <rect
                          width="44"
                          height="44"
                          fill="#000"
                          x="-10"
                          y="-10"
                          fillOpacity="0.001"
                        />
                        <g transform="translate(2, 1)">
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M3.77237 16.035L12.8394 17.928C14.8544 18.337 16.3214 16.946 16.8284 15.479L19.0504 9.03403C19.3724 8.10003 19.2284 7.10303 18.6554 6.30003C18.0824 5.49603 17.1874 5.03403 16.1994 5.03403L10.8274 5.03403L11.8304 2.78003C12.2396 1.86599 11.8899 0.790584 11.0214 0.292028C10.6814 0.0950272 10.3064 2.59237e-05 9.93437 2.58912e-05C9.31437 2.5837e-05 8.70337 0.265027 8.27737 0.764028L3.77837 6.03403L0.000367257 6.03403L0.000366383 16.034L3.77237 16.035Z"
                            fill="white"
                          />
                        </g>
                      </g>
                    ) : (
                      <g
                        id="thumbs-up"
                        onClick={() => {
                          props.sendAdaptiveRadioSignal(
                            'like',
                            currentContext?.uri,
                            currentRow?.track?.uri
                          );
                        }}
                      >
                        <rect
                          width="44"
                          height="44"
                          fill="#000"
                          x="-10"
                          y="-10"
                          fillOpacity="0.001"
                        />
                        <path
                          d="M20.655 7.26584C20.082 6.46184 19.187 6.00084 18.199 6.00084H12.826L13.83 3.74684C14.238 2.82984 13.89 1.75984 13.021 1.25884C12.113 0.73384 10.958 0.93184 10.277 1.73084L5.778 7.00084H2V17.0008H5.772L14.839 18.8938C15.083 18.9428 15.319 18.9668 15.546 18.9668C17.198 18.9668 18.383 17.7348 18.827 16.4448L21.05 9.99984C21.372 9.06584 21.228 8.06984 20.655 7.26584ZM4.971 15.9718H3.029V8.02884H4.971V15.9718ZM20.105 9.67284L17.882 16.1188C17.502 17.2228 16.473 18.2068 15.041 17.9138L6 16.0258V14.0478V9.01684C6 8.53684 6.176 8.06984 6.498 7.69884L11.038 2.37984C11.407 1.94784 12.03 1.84084 12.522 2.12484C12.947 2.36984 13.116 2.89284 12.917 3.33984L11.287 7.00184H18.2C18.86 7.00184 19.458 7.30984 19.842 7.84784C20.224 8.38384 20.32 9.04984 20.105 9.67284Z"
                          fill="white"
                        />
                      </g>
                    )
                  ) : isPremium ? (
                    <g id="repeat">
                      <path
                        d="M8 6H16V8L20.763 5.25L16 2.5V5H8C4.15 5 1 8.15 1 12C1 13.259 1.342 14.44 1.93 15.464L2.792 14.966C2.29 14.09 2 13.079 2 12C2 8.691 4.692 6 8 6ZM22.07 8.537L21.209 9.035C21.71 9.91 22 10.92 22 12C22 15.309 19.308 18 16 18H8V16L3.237 18.75L8 21.5V19H16C19.85 19 23 15.85 23 12C23 10.741 22.658 9.56 22.07 8.537Z"
                        fill="white"
                      />
                    </g>
                  ) : (
                    <path
                      d="M11.499 0.99707C5.70002 0.99707 0.999023 5.69807 0.999023 11.4971C0.999023 17.2961 5.70002 21.9971 11.499 21.9971C17.298 21.9971 21.999 17.2961 21.999 11.4971C21.999 5.69807 17.298 0.99707 11.499 0.99707ZM11.499 20.9971C6.26102 20.9971 1.99902 16.7351 1.99902 11.4971C1.99902 6.25907 6.26102 1.99707 11.499 1.99707C16.737 1.99707 20.999 6.25907 20.999 11.4971C20.999 16.7351 16.737 20.9971 11.499 20.9971ZM5.99902 11.9971H16.999V10.9971H5.99902V11.9971Z"
                      fill="white"
                    />
                  )}
                </g>
              </g>

              <g
                id="next-button"
                style={{ pointerEvents: 'bounding-box' }}
                onClick={() => props.playNext()}
              >
                <rect
                  width="60"
                  height="60"
                  fill="#000"
                  fillOpacity="0.001"
                  x="242"
                  y="628"
                ></rect>

                <path
                  fill="#fff"
                  fillRule="evenodd"
                  d="M279 648v8.237l-14.5-8.371a1 1 0 00-1.5.866v18.536a1 1 0 001.5.866l14.5-8.371V668a1 1 0 001 1h2a1 1 0 001-1v-20a1 1 0 00-1-1h-2a1 1 0 00-1 1z"
                  clipRule="evenodd"
                ></path>
              </g>

              <g
                id="play-button"
                style={{ pointerEvents: 'bounding-box' }}
                onClick={props.togglePlayback}
              >
                {isPlaying ? (
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M188 690C205.673 690 220 675.673 220 658C220 640.327 205.673 626 188 626C170.327 626 156 640.327 156 658C156 675.673 170.327 690 188 690ZM181.6 648.4H185.867V667.6H181.6V648.4ZM196.533 648.4H192.267V667.6H196.533V648.4Z"
                    fill="white"
                  />
                ) : (
                  <path
                    fill="#fff"
                    fillRule="evenodd"
                    d="M220 658c0 17.673-14.327 32-32 32-17.673 0-32-14.327-32-32 0-17.673 14.327-32 32-32 17.673 0 32 14.327 32 32zm-20.715.866L181.5 669.134a1 1 0 01-1.5-.866v-20.536a1 1 0 011.5-.866l17.785 10.268a1 1 0 010 1.732z"
                    clipRule="evenodd"
                  ></path>
                )}
              </g>

              <g
                id="prev-button"
                style={{ pointerEvents: 'bounding-box' }}
                onClick={props.smartPreviousTrack}
              >
                <rect
                  width="60"
                  height="60"
                  fill="#000"
                  fillOpacity="0.001"
                  x="72"
                  y="628"
                ></rect>

                <path
                  fill="#fff"
                  fillRule="evenodd"
                  d="M110.5 647.866L96 656.237V648a1 1 0 00-1-1h-2a1 1 0 00-1 1v20a1 1 0 001 1h2a1 1 0 001-1v-8.237l14.5 8.371a1 1 0 001.5-.866v-18.536a1 1 0 00-1.5-.866z"
                  clipRule="evenodd"
                ></path>
              </g>

              <g id="left-button">
                <g transform="translate(24, 646)">
                  {isInspiredByMix ? (
                    currentContextTrackAdaptiveSignal === 'dislike' ? (
                      <g
                        id="thumbs-down-filled"
                        onClick={() => {
                          // setCurrentTrackFeedback(0);
                        }}
                      >
                        <rect
                          width="44"
                          height="44"
                          fill="#000"
                          x="-10"
                          y="-10"
                          fillOpacity="0.001"
                        />

                        <g transform="translate(1, 5)">
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M16.2281 1.96546L7.16112 0.0724606C5.14612 -0.336539 3.67912 1.05446 3.17212 2.52146L0.950122 8.96646C0.628122 9.90046 0.772122 10.8975 1.34512 11.7005C1.91812 12.5045 2.81312 12.9665 3.80112 12.9665H9.17312L8.17012 15.2205C7.7609 16.1345 8.11059 17.2099 8.97912 17.7085C9.31912 17.9055 9.69412 18.0005 10.0661 18.0005C10.6861 18.0005 11.2971 17.7355 11.7231 17.2365L16.2221 11.9665H20.0001V1.96646L16.2281 1.96546Z"
                            fill="white"
                          />
                        </g>
                      </g>
                    ) : (
                      <g
                        id="thumbs-down"
                        onClick={() => {
                          props.sendAdaptiveRadioSignal(
                            'dislike',
                            currentContext?.uri,
                            currentRow?.track?.uri
                          );
                        }}
                      >
                        <rect
                          width="44"
                          height="44"
                          fill="#000"
                          x="-10"
                          y="-10"
                          fillOpacity="0.001"
                        />
                        <path
                          d="M17.2282 6.99915L8.16117 5.10615C6.14617 4.69715 4.67917 6.08815 4.17217 7.55515L1.95017 14.0002C1.62817 14.9342 1.77217 15.9312 2.34517 16.7342C2.91817 17.5382 3.81317 18.0002 4.80117 18.0002H10.1732L9.17017 20.2542C8.76217 21.1712 9.10917 22.2402 9.97917 22.7422C10.3192 22.9392 10.6942 23.0342 11.0662 23.0342C11.6862 23.0342 12.2972 22.7692 12.7232 22.2702L17.2222 17.0002H21.0002V7.00015H17.2282V6.99915ZM17.0002 9.95215V14.9832C17.0002 15.4632 16.8242 15.9302 16.5032 16.3012L11.9632 21.6202C11.5952 22.0522 10.9712 22.1592 10.4792 21.8752C10.0542 21.6302 9.88417 21.1072 10.0842 20.6602L11.7132 17.0002H4.80117C4.14117 17.0002 3.54317 16.6922 3.16017 16.1542C2.77717 15.6162 2.68117 14.9502 2.89617 14.3262L5.11817 7.88115C5.49917 6.77715 6.52617 5.79315 7.95917 6.08615L17.0002 7.97415V9.95215ZM19.9712 15.9712H18.0292V8.02815H19.9712V15.9712Z"
                          fill="white"
                        />
                      </g>
                    )
                  ) : isPremium ? (
                    <g
                      onClick={() => props.toggleShuffle()}
                      pointerEvents="bounding-box"
                    >
                      <rect
                        width="60"
                        height="60"
                        fill="#000"
                        x="-18"
                        y="-18"
                        fillOpacity="0.001"
                      />
                      {isShuffleEnabled ? (
                        <g id="shuffle-active">
                          <path
                            d="M6.184 8.15999L7.819 10.075L8.476 9.30499L6.944 7.51099C5.708 6.06299 3.906 5.23199 2.001 5.23199V6.23199C3.613 6.23199 5.138 6.93599 6.184 8.15999ZM22.763 17.792L18 15.042V17.292H17.836C17.836 17.292 17.836 17.292 17.835 17.292C16.222 17.292 14.697 16.589 13.651 15.364L12.422 13.925L11.764 14.695L12.89 16.013C14.127 17.461 15.93 18.291 17.835 18.291H17.836H18V20.541L22.763 17.792ZM17.835 6.70899H18V8.95799L22.763 6.20799L18 3.45799V5.70899H17.836C15.93 5.70899 14.128 6.53899 12.89 7.98699L6.184 15.84C5.137 17.065 3.612 17.768 2 17.768V18.768C3.905 18.768 5.707 17.937 6.943 16.49L13.65 8.63699C14.697 7.41099 16.222 6.70899 17.835 6.70899Z"
                            fill="#1DB954"
                          />
                          <circle cx="12" cy="30" r="2" fill="#1DB954" />
                        </g>
                      ) : (
                        <g id="shuffle">
                          <path
                            d="M6.184 8.16001L7.819 10.075L8.476 9.30501L6.944 7.51101C5.708 6.06301 3.906 5.23201 2.001 5.23201V6.23201C3.613 6.23201 5.138 6.93601 6.184 8.16001ZM22.763 17.792L18 15.042V17.292H17.836C17.836 17.292 17.836 17.292 17.835 17.292C16.222 17.292 14.697 16.589 13.651 15.364L12.422 13.925L11.764 14.695L12.89 16.013C14.127 17.461 15.93 18.291 17.835 18.291H17.836H18V20.541L22.763 17.792ZM17.835 6.70901H18V8.95801L22.763 6.20801L18 3.45801V5.70901H17.836C15.93 5.70901 14.128 6.53901 12.89 7.98701L6.184 15.84C5.137 17.065 3.612 17.768 2 17.768V18.768C3.905 18.768 5.707 17.937 6.943 16.49L13.65 8.63701C14.697 7.41101 16.222 6.70901 17.835 6.70901Z"
                            fill="white"
                          />
                        </g>
                      )}
                    </g>
                  ) : (
                    <g
                      onClick={() =>
                        props.toggleSavedStateForUri(currentRow?.track?.uri)
                      }
                    >
                      <rect
                        width="60"
                        height="60"
                        fill="#000"
                        x="-18"
                        y="-18"
                        fillOpacity="0.001"
                      />
                      {isAdded ? (
                        <path
                          d="M20.384 4.53801C18.352 2.50601 15.129 2.39301 12.962 4.19601C12.962 4.19601 12.558 4.54401 12 4.54401C11.426 4.54401 11.038 4.19601 11.038 4.19601C8.87099 2.39301 5.64799 2.50601 3.61599 4.53801C1.46199 6.69201 1.46199 10.185 3.61599 12.339L10.48 20.375C11.278 21.31 12.723 21.31 13.522 20.375L20.386 12.339C22.538 10.185 22.538 6.69301 20.384 4.53801Z"
                          fill="#1DB954"
                        />
                      ) : (
                        <path
                          d="M20.738 4.18488C18.526 1.97288 15.046 1.81188 12.64 3.81388C12.638 3.81588 12.357 4.04388 12 4.04388C11.634 4.04388 11.37 3.82188 11.357 3.81188C8.95503 1.81288 5.47503 1.97288 3.26203 4.18488C0.916031 6.53088 0.916031 10.3469 3.23603 12.6639L10.1 20.6989C10.576 21.2559 11.268 21.5759 12.001 21.5759C12.734 21.5759 13.426 21.2559 13.902 20.6989L20.739 12.6929C23.084 10.3469 23.084 6.53088 20.738 4.18488ZM20.004 12.0149L13.14 20.0499C12.569 20.7189 11.43 20.7189 10.859 20.0499L3.96803 11.9859C2.01303 10.0309 2.01303 6.84789 3.96803 4.89189C4.95103 3.90989 6.23303 3.41288 7.51803 3.41288C8.64503 3.41288 9.77403 3.79488 10.703 4.56888C10.759 4.61688 11.256 5.04388 12 5.04388C12.723 5.04388 13.232 4.62288 13.282 4.58088C15.285 2.91488 18.186 3.04789 20.031 4.89288C21.987 6.84789 21.987 10.0299 20.004 12.0149Z"
                          fill="white"
                        />
                      )}
                    </g>
                  )}
                </g>
              </g>
            </g>

            <g id="play-info" transform="translate(0, -127)">
              {isPremium && (
                <g id="heart" transform="translate(328, 518)">
                  <path
                    d="M20.7378 4.18513C18.5258 1.97313 15.0458 1.81213 12.6398 3.81413C12.6378 3.81613 12.3568 4.04413 11.9998 4.04413C11.6338 4.04413 11.3698 3.82213 11.3568 3.81213C8.95479 1.81313 5.47479 1.97313 3.26179 4.18513C0.915787 6.53113 0.915787 10.3471 3.23579 12.6641L10.0998 20.6991C10.5758 21.2561 11.2678 21.5761 12.0008 21.5761C12.7338 21.5761 13.4258 21.2561 13.9018 20.6991L20.7388 12.6931C23.0838 10.3471 23.0838 6.53113 20.7378 4.18513ZM20.0038 12.0151L13.1398 20.0501C12.5688 20.7191 11.4298 20.7191 10.8588 20.0501L3.96779 11.9861C2.01279 10.0311 2.01279 6.84813 3.96779 4.89213C4.95079 3.91013 6.23279 3.41313 7.51779 3.41313C8.64479 3.41313 9.77379 3.79513 10.7028 4.56913C10.7588 4.61713 11.2558 5.04413 11.9998 5.04413C12.7228 5.04413 13.2318 4.62313 13.2818 4.58113C15.2848 2.91513 18.1858 3.04813 20.0308 4.89313C21.9868 6.84813 21.9868 10.0301 20.0038 12.0151Z"
                    fill="white"
                  />
                </g>
              )}

              <g fill="#fff">
                <text
                  fillOpacity="0.7"
                  xmlSpace="preserve"
                  style={{ whiteSpace: 'pre' }}
                  fontSize="16"
                  letterSpacing="0"
                >
                  <tspan x="24" y="563.24">
                    {truncate(currentRow?.track?.artists?.[0]?.name)}
                  </tspan>
                </text>
                <text
                  xmlSpace="preserve"
                  style={{ whiteSpace: 'pre' }}
                  fontSize={currentRow?.track?.name?.length > 18 ? 20 : 24}
                  fontWeight="bold"
                  letterSpacing="-0.96"
                >
                  <tspan x="24" y="535.86">
                    {truncate(currentRow?.track?.name, 30)}
                  </tspan>
                </text>
              </g>
            </g>

            <g id="navigation-bar" transform="translate(0, -13.017)">
              <g
                id="context-menu-button"
                style={{ pointerEvents: 'bounding-box' }}
                onClick={() =>
                  props.openContextMenu(
                    currentRow?.track?.uri,
                    currentRecommendationsUri
                  )
                }
              >
                <rect
                  width="60"
                  height="60"
                  fill="#000"
                  fillOpacity="0.001"
                  x="315"
                  y="40"
                />
                <path
                  fill="#fff"
                  fillRule="evenodd"
                  d="M339.5 64.5c-.8 0-1.5.7-1.5 1.5s.7 1.5 1.5 1.5 1.5-.7 1.5-1.5-.7-1.5-1.5-1.5zm15 0c-.8 0-1.5.7-1.5 1.5s.7 1.5 1.5 1.5 1.5-.7 1.5-1.5-.7-1.5-1.5-1.5zm-7.5 0c-.8 0-1.5.7-1.5 1.5s.7 1.5 1.5 1.5 1.5-.7 1.5-1.5-.7-1.5-1.5-1.5z"
                  clipRule="evenodd"
                ></path>
              </g>

              <text
                fill="#fff"
                xmlSpace="preserve"
                style={{ whiteSpace: 'pre' }}
                fontSize="13"
                fontWeight="bold"
                letterSpacing="0"
                onClick={() => {
                  if (stationId) {
                    props.toggleNowPlayingView();
                    history.push(`/station/${stationId}`);
                  } else if (
                    getTypeFromUri(currentContext?.uri) === 'playlist'
                  ) {
                    props.toggleNowPlayingView();
                    history.push(
                      currentContext.uri
                        .replace(/^spotify/, '')
                        ?.replace(/:/g, '/')
                    );
                  }
                }}
              >
                <tspan x="187.5" y="70.257" textAnchor="middle">
                  {currentContextDescription}
                </tspan>
              </text>

              <g
                id="minimize-button"
                style={{ pointerEvents: 'bounding-box' }}
                onClick={props.toggleNowPlayingView}
              >
                <rect
                  width="60"
                  height="60"
                  fill="#000"
                  fillOpacity="0.001"
                  x="0"
                  y="40"
                />

                <path
                  fill="#fff"
                  fillRule="evenodd"
                  d="M28 71.77l-8.921-10.445.76-.65L28 70.23l8.161-9.555.76.65L28 71.77z"
                  clipRule="evenodd"
                ></path>
              </g>
            </g>
          </svg>
        </div>

        {/* {activeTrackRowRecommendations &&
                      activeTrackRowRecommendations.uri === uri && (
                        <Flipped
                          key="track-row-recommendations"
                          flipId="track-row-recommendations"
                          onAppear={onAppear}
                          onExit={onExit}
                        > */}
        <div
          className={`NowPlayingView__track-row-recommendations-wrapper ${
            trackRowRecommendations
              ? 'NowPlayingView__track-row-recommendations-wrapper--is-visible'
              : ''
          }`}
          style={{ backgroundColor: currentTrackColors?.VIBRANT }}
        >
          {/* <TrackRowRecommendations
            uri={currentRecommendationsUri?.substring?.(0, 36)}
            {...trackRowRecommendations}
            onPlayRecommendation={(uri) =>
              uri === currentPreviewUri && isPreviewPlaying
                ? props.pausePreview(uri)
                : props.playPreview(uri)
            }
            inNPV
            contextUri={currentRecommendationsUri}
            contextName={currentContext?.metadata?.context_description}
          /> */}
        </div>
        {/* </Flipped>
                      )} */}
      </div>
    </div>
  );
}
